import { useEffect, useRef, useState } from "react";

const EASE = "cubic-bezier(0.28, 0.11, 0.32, 1)";

// Scroll reveal: fades + lifts an element into place once, using IntersectionObserver only.
export const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [state, setState] = useState("hidden");

  useEffect(() => {
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || typeof IntersectionObserver === "undefined") {
      setState("instant");
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
            setState("shown");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (state === "instant") return { ref, style: undefined };

  const ms = typeof delay === "function" ? delay() : delay;
  const shown = state === "shown";

  return {
    ref,
    style: {
      opacity: shown ? 1 : 0,
      transform: shown ? "none" : "translateY(40px)",
      transition: `opacity 900ms ${EASE} ${ms}ms, transform 900ms ${EASE} ${ms}ms`,
      willChange: "opacity, transform",
    },
  };
};

export default useReveal;
